import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { OfficialsAddonAdmin } from '@/components/officials/OfficialsAddonAdmin';
import { SalesTab } from '@/components/admin/billing/SalesTab';
import { GrowthTab } from '@/components/admin/growth/GrowthTab';
import { CreateCouponForm } from '@/components/admin/coupons/CreateCouponForm';
import { CouponList } from '@/components/admin/coupons/CouponList';
import { UsersTable } from '@/components/admin/users/UsersTable';
import { CapabilitiesDocButton } from '@/components/admin/CapabilitiesDocButton';
import {
  ChevronLeft, Users, Calendar, Ticket, TrendingUp, Loader2, Shield, UserPlus, Mail, Send, History, LifeBuoy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, isAdmin, isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'overview');
  
  // New user form
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    password: '',
    subscription: 'free'
  });
  const [creatingUser, setCreatingUser] = useState(false);

  // Email broadcast state
  const [broadcast, setBroadcast] = useState({
    subject: '',
    body: '',
    audience: 'all'
  });
  const [audienceCounts, setAudienceCounts] = useState({ all: 0, pro: 0, free: 0 });
  const [broadcasts, setBroadcasts] = useState([]);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingBroadcast, setSendingBroadcast] = useState(false);

  useEffect(() => {
    // Wait for auth loading to complete
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!isAdmin) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchData();
  }, [isAuthenticated, isAdmin, navigate, authLoading]);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('session_token')}`
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, couponsRes, audienceRes, broadcastsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/coupons`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/broadcast/audience-count?audience=all`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/broadcasts`, { headers: getAuthHeaders() })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (couponsRes.ok) {
        const couponsData = await couponsRes.json();
        setCoupons(couponsData.coupons || []);
      }
      if (audienceRes.ok) {
        const ad = await audienceRes.json();
        setAudienceCounts(ad.counts || { all: 0, pro: 0, free: 0 });
      }
      if (broadcastsRes.ok) {
        const bd = await broadcastsRes.json();
        setBroadcasts(bd.broadcasts || []);
      }
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCoupon = async (couponId, isActive) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/coupons/${couponId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_active: !isActive })
      });

      if (!response.ok) throw new Error('Failed to update coupon');
      toast.success(`Coupon ${isActive ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newUser.email.trim() || !newUser.name.trim() || !newUser.password.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (newUser.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setCreatingUser(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          email: newUser.email.trim(),
          name: newUser.name.trim(),
          password: newUser.password,
          subscription: newUser.subscription
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to create user');

      toast.success(`User ${newUser.email} created successfully!`);
      setNewUser({ email: '', name: '', password: '', subscription: 'free' });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreatingUser(false);
    }
  };

  const handleSendBroadcast = async ({ testOnly }) => {
    if (!broadcast.subject.trim() || !broadcast.body.trim()) {
      toast.error('Subject and message body are required');
      return;
    }
    if (testOnly) {
      setSendingTest(true);
    } else {
      const audienceCount = audienceCounts[broadcast.audience] || 0;
      if (audienceCount === 0) {
        toast.error('No recipients in this audience');
        return;
      }
      const confirmMsg = `Send "${broadcast.subject}" to ${audienceCount} ${broadcast.audience} user${audienceCount !== 1 ? 's' : ''}?\n\nThis cannot be undone.`;
      if (!window.confirm(confirmMsg)) return;
      setSendingBroadcast(true);
    }
    try {
      const response = await fetch(`${API_URL}/api/admin/broadcast`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          subject: broadcast.subject,
          body: broadcast.body,
          audience: broadcast.audience,
          test_only: testOnly
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Broadcast failed');
      if (testOnly) {
        toast.success(`Test email sent to ${user?.email}`);
      } else {
        const b = data.broadcast || {};
        toast.success(`Broadcast sent to ${b.recipients_sent || 0} / ${b.recipients_total || 0} users`);
        setBroadcast({ subject: '', body: '', audience: broadcast.audience });
      }
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Broadcast failed');
    } finally {
      setSendingTest(false);
      setSendingBroadcast(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00247D]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-[#00247D] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-3 h-16 pr-32 sm:pr-36 min-[1600px]:pr-0">
            <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white whitespace-nowrap" data-testid="admin-back-link">
              <ChevronLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back to App</span>
            </Link>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              <h1 className="text-xl font-bold whitespace-nowrap" style={{ fontFamily: 'Barlow Condensed' }}>
                ADMIN DASHBOARD
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <CapabilitiesDocButton />
              <Link to="/admin/support" className="flex items-center gap-1.5 text-sm text-white/80 hover:text-white" data-testid="admin-support-link">
                <LifeBuoy className="w-4 h-4" />
                <span className="hidden sm:inline">Support</span>
              </Link>
              <div className="hidden lg:block text-sm text-white/70">
                {user?.email}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Users</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_users || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Pro Users</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.pro_users || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Calendar className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Events</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_events || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Ticket className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Active Coupons</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.active_coupons || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['overview', 'growth', 'sales', 'coupons', 'users', 'broadcast', 'officials'].map(tab => (
            <Button
              key={tab}
              variant={activeTab === tab ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab)}
              className={activeTab === tab ? 'bg-[#00247D]' : ''}
              data-testid={`tab-${tab}`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </Button>
          ))}
        </div>

        {/* Content */}
        {activeTab === 'coupons' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <CreateCouponForm onCreated={fetchData} />
            <CouponList coupons={coupons} onToggle={handleToggleCoupon} />
          </div>
        )}

        {activeTab === 'users' && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Create User Form */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  Create User
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateUser} className="space-y-4">
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      placeholder="user@example.com"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      className="mt-1"
                      data-testid="new-user-email"
                    />
                  </div>
                  <div>
                    <Label>Name *</Label>
                    <Input
                      placeholder="Full Name"
                      value={newUser.name}
                      onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                      className="mt-1"
                      data-testid="new-user-name"
                    />
                  </div>
                  <div>
                    <Label>Password *</Label>
                    <Input
                      type="password"
                      placeholder="Min. 6 characters"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      className="mt-1"
                      data-testid="new-user-password"
                    />
                  </div>
                  <div>
                    <Label>Subscription</Label>
                    <Select
                      value={newUser.subscription}
                      onValueChange={(value) => setNewUser({ ...newUser, subscription: value })}
                    >
                      <SelectTrigger className="mt-1" data-testid="new-user-subscription">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="pro_monthly">Pro Monthly</SelectItem>
                        <SelectItem value="pro_annual">Pro Annual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-[#00247D] hover:bg-[#001A5C]"
                    disabled={creatingUser}
                    data-testid="create-user-btn"
                  >
                    {creatingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create User'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <UsersTable />
          </div>
        )}

        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Quick Stats</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-slate-600">Free Users</span>
                    <span className="font-bold">{stats?.free_users || 0}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-slate-600">Pro Users</span>
                    <span className="font-bold text-amber-600">{stats?.pro_users || 0}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-slate-600">Conversion Rate</span>
                    <span className="font-bold text-green-600">
                      {stats?.total_users > 0 
                        ? ((stats?.pro_users / stats?.total_users) * 100).toFixed(1) 
                        : 0}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-slate-600">Events Created</span>
                    <span className="font-bold">{stats?.total_events || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Recent Coupons</CardTitle>
              </CardHeader>
              <CardContent>
                {coupons.slice(0, 5).map(coupon => (
                  <div key={coupon.coupon_id} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                    <code className="font-mono text-[#00247D]">{coupon.code}</code>
                    <span className="text-sm text-slate-500">
                      {coupon.uses_count}/{coupon.max_uses} used
                    </span>
                  </div>
                ))}
                {coupons.length === 0 && (
                  <p className="text-slate-500 text-center py-4">No coupons yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'broadcast' && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="broadcast-tab">
            {/* Compose broadcast */}
            <Card className="border-slate-200 lg:col-span-3">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mail className="w-5 h-5 text-[#00247D]" />
                  Send Email Broadcast
                </CardTitle>
                <p className="text-sm text-slate-500">
                  Send a branded Schedule Guru email to your users. Use the toolbar to format — bold / italic / lists / links / headings.
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label>Audience</Label>
                    <Select
                      value={broadcast.audience}
                      onValueChange={(value) => setBroadcast({ ...broadcast, audience: value })}
                    >
                      <SelectTrigger className="mt-1" data-testid="broadcast-audience">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Users ({audienceCounts.all})</SelectItem>
                        <SelectItem value="pro">Pro Users ({audienceCounts.pro})</SelectItem>
                        <SelectItem value="free">Free Users ({audienceCounts.free})</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-400 mt-1">
                      {audienceCounts[broadcast.audience] || 0} recipient{(audienceCounts[broadcast.audience] || 0) !== 1 ? 's' : ''} will receive this email.
                    </p>
                  </div>
                  <div>
                    <Label>Subject *</Label>
                    <Input
                      placeholder="e.g., New tournament formats now available!"
                      value={broadcast.subject}
                      onChange={(e) => setBroadcast({ ...broadcast, subject: e.target.value })}
                      className="mt-1"
                      maxLength={140}
                      data-testid="broadcast-subject"
                    />
                    <p className="text-xs text-slate-400 mt-1">{broadcast.subject.length}/140 characters</p>
                  </div>
                  <div>
                    <Label>Message Body *</Label>
                    <div className="mt-1">
                      <RichTextEditor
                        value={broadcast.body}
                        onChange={(html) => setBroadcast({ ...broadcast, body: html })}
                        placeholder="Hey there! Big news — we just shipped AI Box Score Scanning. Snap a photo and we'll auto-build the whole box score for you."
                        testId="broadcast-body"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      The message will be wrapped in the Schedule Guru branded template (logo, greeting, footer). Hit <kbd className="font-mono">Enter</kbd> for a new paragraph; use the toolbar for formatting.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleSendBroadcast({ testOnly: true })}
                      disabled={sendingTest || sendingBroadcast || !broadcast.subject.trim() || !broadcast.body.trim()}
                      data-testid="broadcast-send-test-btn"
                    >
                      {sendingTest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                      Send Test to Me
                    </Button>
                    <Button
                      type="button"
                      className="bg-[#009E49] hover:bg-[#008840] text-white"
                      onClick={() => handleSendBroadcast({ testOnly: false })}
                      disabled={sendingTest || sendingBroadcast || !broadcast.subject.trim() || !broadcast.body.trim()}
                      data-testid="broadcast-send-btn"
                    >
                      {sendingBroadcast ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Send Broadcast
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* History */}
            <Card className="border-slate-200 lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <History className="w-5 h-5 text-slate-500" />
                  Broadcast History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {broadcasts.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-8">No broadcasts sent yet.</p>
                ) : (
                  <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1" data-testid="broadcast-history-list">
                    {broadcasts.map(b => (
                      <div key={b.broadcast_id} className="p-3 rounded-lg border border-slate-200 hover:bg-slate-50">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-sm text-slate-800 truncate max-w-[220px]" title={b.subject}>
                            {b.subject}
                          </span>
                          {b.test_only ? (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 uppercase tracking-wide">Test</span>
                          ) : (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 uppercase tracking-wide">Live</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {b.audience === 'all' ? 'All users' : b.audience === 'pro' ? 'Pro users' : 'Free users'}
                          {' '}&middot;{' '}
                          <span className="text-green-600 font-medium">{b.recipients_sent}</span>
                          /{b.recipients_total} sent
                          {b.recipients_failed > 0 && (
                            <span className="text-red-600"> &middot; {b.recipients_failed} failed</span>
                          )}
                        </p>
                        <p className="text-[11px] text-slate-400 mt-1">
                          {b.created_at ? new Date(b.created_at).toLocaleString() : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'officials' && <OfficialsAddonAdmin />}

        {activeTab === 'sales' && <SalesTab />}
        {activeTab === 'growth' && <GrowthTab />}
      </main>
    </div>
  );
}
